import type { ReactNode } from 'react'
import { AlertWithClose } from '@/components/ui/Alert'
import { Text } from '@/components/ui/Text'

export type IdentityBlockedStatus = 'NO_EMAIL_PENDING' | 'NO_EMAIL_ARCHIVED' | 'CONFLICT_PENDING'

export interface IdentityBlockedBannerProps {
  status: IdentityBlockedStatus
  /** Optional action slot — e.g. a "Capture email" button (FR-F3a), gated by the caller's own `<Can>`. */
  action?: ReactNode
}

const COPY: Record<IdentityBlockedStatus, string> = {
  NO_EMAIL_PENDING:
    'This person has no email on file. They cannot be given new appointments, worker records, ' +
    'contacts or memberships until an email is captured.',
  NO_EMAIL_ARCHIVED:
    'This person has no email on file and is archived. They cannot be given new appointments, ' +
    'worker records, contacts or memberships until an email is captured.',
  CONFLICT_PENDING:
    "This person's email is shared with another identity record. They cannot be given new " +
    'appointments, worker records, contacts or memberships until the conflict is resolved.',
}

/** `destructive` for the conflict case — it needs admin resolution, not just an email capture. */
const VARIANT: Record<IdentityBlockedStatus, 'warning' | 'destructive'> = {
  NO_EMAIL_PENDING: 'warning',
  NO_EMAIL_ARCHIVED: 'warning',
  CONFLICT_PENDING: 'destructive',
}

/**
 * IdentityBlockedBanner — PRD FR-F3, FR-16(a,e). Shown on person detail only for the two
 * blocked `identityStatus` values; explains the restriction and carries an optional action slot
 * (FR-F3a's "Capture email" button, gated by the caller under `<Can k={PersonAclKeys.identityWrite}>`).
 *
 * PRESENTATIONAL ONLY in F1M2FE-T01 — no app-code consumer yet (T05/T06/T11 wire this up against
 * `PersonDetailPage`).
 *
 * Not closable: the restriction is a fact about the data, not a transient message — dismissing the
 * banner would hide the reason the page's "Capture email" affordance exists without changing
 * anything about the person's actual blocked state.
 */
export function IdentityBlockedBanner({ status, action }: IdentityBlockedBannerProps) {
  return (
    <AlertWithClose variant={VARIANT[status]} closable={false}>
      <div className="flex flex-col gap-2">
        <Text variant="body-sm" color={VARIANT[status] === 'destructive' ? 'danger' : 'warning'}>
          {COPY[status]}
        </Text>
        {action}
      </div>
    </AlertWithClose>
  )
}
